import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../components/common/Button";
import DataTable from "../../components/common/DataTable";
import DatePicker from "../../components/common/DatePicker";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import reportService from "../../services/reportService";

function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function formatWeight(value) {
  return `${formatNumber(value)} kg`;
}

function formatCurrency(value) {
  return `₹ ${formatNumber(value)}`;
}

function formatUSD(value) {
  return `$ ${formatNumber(value)}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB");
}

function formatDateISO(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getDatesForPreset(preset) {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = now.getMonth();

  switch (preset) {
    case "current_month": {
      const start = new Date(yyyy, mm, 1);
      const end = new Date(yyyy, mm + 1, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "last_2_months": {
      const start = new Date(yyyy, mm - 1, 1);
      const end = new Date(yyyy, mm + 1, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "last_3_months": {
      const start = new Date(yyyy, mm - 2, 1);
      const end = new Date(yyyy, mm + 1, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "last_quarter": {
      const currentQuarter = Math.floor(mm / 3);
      const targetQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const targetYear = currentQuarter === 0 ? yyyy - 1 : yyyy;
      const start = new Date(targetYear, targetQuarter * 3, 1);
      const end = new Date(targetYear, (targetQuarter + 1) * 3, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "half_year": {
      const start = new Date(yyyy, mm - 5, 1);
      const end = new Date(yyyy, mm + 1, 0);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    case "full_year": {
      const start = new Date(yyyy, 0, 1);
      const end = new Date(yyyy, 11, 31);
      return { startDate: formatDateISO(start), endDate: formatDateISO(end) };
    }
    default:
      return { startDate: "", endDate: "" };
  }
}

const presetOptions = [
  { value: "current_month", label: "Current Month" },
  { value: "last_2_months", label: "Last 2 Months" },
  { value: "last_3_months", label: "Last 3 Months" },
  { value: "last_quarter", label: "Last Quarter" },
  { value: "half_year", label: "Half Year" },
  { value: "full_year", label: "Full Year" },
  { value: "custom", label: "Custom Date" },
];

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function PnLReportPage() {
  const [reportData, setReportData] = useState({
    summary: {},
    purchases: [],
    expenseMaster: [],
    overheadExpenses: [],
    labourExpenses: [],
    sales: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("purchases");
  const [expandedSalesRows, setExpandedSalesRows] = useState(new Set());
  const [preset, setPreset] = useState("");

  // Date Filters
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
  });

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const res = await reportService.getPnLReport(params);
      setReportData({
        summary: res.summary || {},
        purchases: res.purchases || [],
        expenseMaster: res.expenseMaster || [],
        overheadExpenses: res.overheadExpenses || [],
        labourExpenses: res.labourExpenses || [],
        sales: res.sales || [],
      });
    } catch (err) {
      setReportData({
        summary: {},
        purchases: [],
        expenseMaster: [],
        overheadExpenses: [],
        labourExpenses: [],
        sales: [],
      });
      setError(err?.message || "Failed to load Profit and Loss report.");
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handlePresetChange = (e) => {
    const selectedPreset = e.target.value;
    setPreset(selectedPreset);

    if (!selectedPreset) {
      setFilters({ startDate: "", endDate: "" });
      return;
    }

    if (selectedPreset === "custom") {
      return;
    }

    const { startDate, endDate } = getDatesForPreset(selectedPreset);
    setFilters({ startDate, endDate });
  };

  const handleClearFilters = () => {
    setPreset("");
    setFilters({ startDate: "", endDate: "" });
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    setError("");
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const blob = await reportService.downloadPnLReport(format, params);
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      downloadBlob(blob, `pnl-report.${ext}`);
    } catch (err) {
      setError(err?.message || `Failed to download ${format.toUpperCase()} report.`);
    } finally {
      setDownloadingFormat("");
    }
  };

  const toggleSalesRow = (id) => {
    const newExpanded = new Set(expandedSalesRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSalesRows(newExpanded);
  };

  const summary = reportData.summary || {};
  const isProfit = summary.isProfit ?? (summary.netProfitOrLoss >= 0);

  // Tab 1: Total Purchases Columns
  const purchaseColumns = useMemo(
    () => [
      {
        key: "billNo",
        label: "Purchase Bill No",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">
            {item.billNo || "-"}
          </span>
        ),
      },
      {
        key: "bookingNo",
        label: "Booking No",
        sortable: true,
        render: (item) => (
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {item.bookingNo || "-"}
          </span>
        ),
      },
      {
        key: "billDate",
        label: "Bill Date",
        sortable: true,
        render: (item) => formatDate(item.billDate),
      },
      {
        key: "vendorName",
        label: "Vendor Name",
        sortable: true,
        className: "max-w-[150px] truncate",
      },
      {
        key: "totalWeight",
        label: "Total Weight (KG)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-medium",
        render: (item) => formatWeight(item.totalWeight),
      },
      {
        key: "totalPriceUSD",
        label: "USD Amount ($)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-medium text-blue-600 dark:text-blue-400",
        render: (item) => formatUSD(item.totalPriceUSD),
      },
      {
        key: "totalPriceRS",
        label: "RS Amount (RS)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-bold text-slate-900 dark:text-white",
        render: (item) => formatCurrency(item.totalPriceRS),
      },
    ],
    []
  );

  // Tab 2: Expense Master Columns
  const expenseMasterColumns = useMemo(
    () => [
      {
        key: "blNumber",
        label: "BL Number",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">
            {item.blNumber || "-"}
          </span>
        ),
      },
      {
        key: "expenseCategoryName",
        label: "Expense Category",
        sortable: true,
        render: (item) => (
          <span className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 text-xs font-semibold text-purple-700 dark:text-purple-300">
            {item.expenseCategoryName || "-"}
          </span>
        ),
      },
      {
        key: "price",
        label: "Price (RS)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-bold text-amber-600 dark:text-amber-400",
        render: (item) => formatCurrency(item.price),
      },
      {
        key: "remark",
        label: "Remark",
        render: (item) => (
          <span className="text-slate-500 dark:text-slate-400 italic text-xs">
            {item.remark || "-"}
          </span>
        ),
      },
      {
        key: "createdDate",
        label: "Created Date",
        sortable: true,
        render: (item) => formatDate(item.createdDate),
      },
    ],
    []
  );

  // Tab 3: Overhead Expenses Columns
  const overheadColumns = useMemo(
    () => [
      {
        key: "overheadDetails",
        label: "Overhead Details",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">
            {item.overheadDetails || "-"}
          </span>
        ),
      },
      {
        key: "expenseCategoryName",
        label: "Expense Category",
        sortable: true,
        render: (item) => (
          <span className="inline-flex items-center rounded-md bg-indigo-50 dark:bg-indigo-900/20 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
            {item.expenseCategoryName || "-"}
          </span>
        ),
      },
      {
        key: "price",
        label: "Price (RS)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-bold text-slate-900 dark:text-white",
        render: (item) => formatCurrency(item.price),
      },
      {
        key: "remark",
        label: "Remark",
        render: (item) => (
          <span className="text-slate-500 dark:text-slate-400 italic text-xs">
            {item.remark || "-"}
          </span>
        ),
      },
      {
        key: "createdDate",
        label: "Created Date",
        sortable: true,
        render: (item) => formatDate(item.createdDate),
      },
    ],
    []
  );

  // Tab 4: Labour Expenses Columns
  const labourColumns = useMemo(
    () => [
      {
        key: "lotNo",
        label: "Lot No",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">
            {item.lotNo || "-"}
          </span>
        ),
      },
      {
        key: "yardNo",
        label: "Yard No",
        sortable: true,
        render: (item) => (
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {item.yardNo || "-"}
          </span>
        ),
      },
      {
        key: "date",
        label: "Processing Date",
        sortable: true,
        render: (item) => formatDate(item.date),
      },
      {
        key: "processedWeight",
        label: "Processed Weight (KG)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-medium",
        render: (item) => formatWeight(item.processedWeight),
      },
      {
        key: "labourRate",
        label: "Labour Rate (RS/KG)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-medium text-slate-600 dark:text-slate-400",
        render: (item) => formatCurrency(item.labourRate),
      },
      {
        key: "totalLabourCost",
        label: "Total Labour Cost (RS)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-bold text-emerald-600 dark:text-emerald-400",
        render: (item) => formatCurrency(item.totalLabourCost),
      },
    ],
    []
  );

  // Tab 5: Total Sales Columns
  const salesColumns = useMemo(
    () => [
      {
        key: "expand",
        label: "",
        width: "40px",
        render: (item) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleSalesRow(item.sellingMasterId);
            }}
            className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <i
              className={`material-icons text-sm transition-transform duration-200 ${
                expandedSalesRows.has(item.sellingMasterId) ? "rotate-180" : ""
              }`}
            >
              expand_more
            </i>
          </button>
        ),
      },
      {
        key: "invoiceNo",
        label: "Invoice No",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">
            {item.invoiceNo || "-"}
          </span>
        ),
      },
      {
        key: "customerName",
        label: "Customer Name",
        sortable: true,
        className: "max-w-[140px] truncate",
      },
      {
        key: "companyName",
        label: "Company Name",
        sortable: true,
        className: "max-w-[140px] truncate",
      },
      {
        key: "date",
        label: "Sale Date",
        sortable: true,
        render: (item) => formatDate(item.date),
      },
      {
        key: "totalAmount",
        label: "Base Amount",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-medium text-slate-700 dark:text-slate-300",
        render: (item) => formatCurrency(item.totalAmount),
      },
      {
        key: "roundOff",
        label: "TCS / RoundOff",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right text-xs italic text-slate-500 dark:text-slate-400",
        render: (item) => {
          const ro = parseFloat(item.roundOff) || 0;
          if (ro > 0) return `+₹${formatNumber(ro)}`;
          if (ro < 0) return `-₹${formatNumber(Math.abs(ro))}`;
          return `₹0.00`;
        },
      },
      {
        key: "grandTotal",
        label: "Grand Total",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-bold text-primary-700 dark:text-primary-300",
        render: (item) => formatCurrency(item.grandTotal),
      },
    ],
    [expandedSalesRows]
  );

  const renderExpandableSalesContent = (sale) => (
    <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-900/50 border-y border-slate-100 dark:border-slate-800">
      <div className="flex items-center gap-2 mb-3">
        <i className="material-icons text-sm text-primary-600">inventory_2</i>
        <h3 className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">
          Selling Materials Breakdown
        </h3>
      </div>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <table className="w-full min-w-[600px] text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400">
                  Yard
                </th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400">
                  Lot
                </th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400">
                  Stock Category
                </th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400">
                  Description
                </th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400 text-right">
                  Qty (KG)
                </th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400 text-right">
                  Rate (RS/KG)
                </th>
                <th className="px-3 py-2 text-[9px] font-black uppercase text-slate-400 text-right">
                  Amount (RS)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {(sale.sellingMaterials || []).map((m, idx) => (
                <tr
                  key={idx}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                >
                  <td className="px-3 py-2 text-[11px] font-medium text-slate-700 dark:text-slate-300">
                    {m.yard || "-"}
                  </td>
                  <td className="px-3 py-2 text-[11px] font-medium text-slate-700 dark:text-slate-300">
                    {m.lot || "-"}
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center rounded-md bg-primary-50 dark:bg-primary-900/20 px-1.5 py-0.5 text-[9px] font-bold text-primary-700 dark:text-primary-300 uppercase tracking-tight">
                      {m.stockCategory || "-"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-slate-500 dark:text-slate-400 italic">
                    {m.description || "No description"}
                  </td>
                  <td className="px-3 py-2 text-[11px] font-bold text-slate-900 dark:text-white text-right">
                    {formatNumber(m.quantity)} kg
                  </td>
                  <td className="px-3 py-2 text-[11px] font-bold text-slate-600 dark:text-slate-400 text-right">
                    {formatCurrency(m.rate || 0)}
                  </td>
                  <td className="px-3 py-2 text-[11px] font-bold text-primary-600 dark:text-primary-400 text-right">
                    {formatCurrency(m.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="w-full lg:w-64 shrink-0">
          <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm h-full flex flex-col justify-between">
            <div>
              <h4 className="text-[10px] font-black uppercase text-slate-500 mb-3 border-b border-slate-100 dark:border-slate-800 pb-2">
                Invoice Financials
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-slate-500">Base Amount</span>
                  <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                    {formatCurrency(sale.totalAmount)}
                  </span>
                </div>
                {sale.roundOff !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-slate-500">Round Off</span>
                    <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 italic">
                      {(() => {
                        const ro = parseFloat(sale.roundOff) || 0;
                        if (ro > 0) return `+₹${formatNumber(ro)}`;
                        if (ro < 0) return `-₹${formatNumber(Math.abs(ro))}`;
                        return `₹0.00`;
                      })()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
              <span className="text-[10px] font-black text-slate-700 dark:text-slate-300">
                GRAND TOTAL
              </span>
              <span className="text-[13px] font-black text-primary-600 dark:text-primary-400">
                {formatCurrency(sale.grandTotal)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    {
      id: "purchases",
      label: "Total Purchases",
      count: reportData.purchases.length,
      icon: "shopping_cart",
    },
    {
      id: "expenseMaster",
      label: "Expense Master",
      count: reportData.expenseMaster.length,
      icon: "account_balance_wallet",
    },
    {
      id: "overheadExpenses",
      label: "Overhead Expenses",
      count: reportData.overheadExpenses.length,
      icon: "receipt_long",
    },
    {
      id: "labourExpenses",
      label: "Labour Expenses",
      count: reportData.labourExpenses.length,
      icon: "engineering",
    },
    {
      id: "sales",
      label: "Total Sales",
      count: reportData.sales.length,
      icon: "sell",
    },
  ];

  return (
    <div className="space-y-5">
      {/* Header Section */}
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Reports
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-bold text-slate-950 dark:text-white">
            Profit &amp; Loss (P&amp;L) Report
          </h1>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-end lg:gap-3">
          <Button
            variant="secondary"
            onClick={loadReport}
            isLoading={isLoading}
            className="col-span-2 sm:col-auto"
          >
            <span className="flex items-center justify-center gap-1.5">
              <i className="material-icons text-sm">refresh</i>
              <span className="sm:inline">Apply Filter</span>
            </span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("xlsx")}
            isLoading={downloadingFormat === "xlsx"}
            disabled={Boolean(downloadingFormat)}
            className="flex-1 sm:flex-none"
          >
            <span className="flex items-center justify-center gap-1.5 text-emerald-600">
              <i className="material-icons text-sm">grid_on</i>
              <span className="text-[10px] sm:text-xs font-bold">EXPORT EXCEL</span>
            </span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleDownload("pdf")}
            isLoading={downloadingFormat === "pdf"}
            disabled={Boolean(downloadingFormat)}
            className="flex-1 sm:flex-none"
          >
            <span className="flex items-center justify-center gap-1.5 text-rose-600">
              <i className="material-icons text-sm">picture_as_pdf</i>
              <span className="text-[10px] sm:text-xs font-bold">EXPORT PDF</span>
            </span>
          </Button>
        </div>
      </div>

      {/* Date Range Filter Bar */}
      <div
        className={`grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all ${
          preset === "custom"
            ? "sm:grid-cols-2 lg:grid-cols-4"
            : "sm:grid-cols-2 lg:grid-cols-3"
        }`}
      >
        <SearchableDropdown
          label="Period / Preset Filter"
          name="preset"
          value={preset}
          onChange={handlePresetChange}
          options={presetOptions}
          placeholder="Select Period Range"
        />

        {preset === "custom" && (
          <>
            <DatePicker
              label="From Date"
              value={filters.startDate}
              onChange={(val) => {
                setFilters((prev) => ({ ...prev, startDate: val }));
              }}
              placeholder="Select start date"
            />
            <DatePicker
              label="To Date"
              value={filters.endDate}
              onChange={(val) => {
                setFilters((prev) => ({ ...prev, endDate: val }));
              }}
              placeholder="Select end date"
            />
          </>
        )}

        <div className="flex items-end">
          <Button
            variant="secondary"
            className="w-full h-[38px] text-xs font-medium"
            onClick={handleClearFilters}
          >
            Clear Filter
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-medium text-danger">
          {error}
        </div>
      )}

      {/* Executive Summary KPI Cards (Header) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/* Card 1: Total Revenue */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Total Revenue (Sales)
            </p>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
              <i className="material-icons text-lg">trending_up</i>
            </span>
          </div>
          <p className="mt-2 text-xl sm:text-2xl font-black text-slate-950 dark:text-white">
            {formatCurrency(summary.totalRevenue ?? summary.totalSalesGrandTotal ?? 0)}
          </p>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Base: {formatCurrency(summary.totalSalesBaseAmount ?? 0)}
          </p>
        </div>

        {/* Card 2: Total Expenses */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Total Expenses
            </p>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400">
              <i className="material-icons text-lg">trending_down</i>
            </span>
          </div>
          <p className="mt-2 text-xl sm:text-2xl font-black text-slate-950 dark:text-white">
            {formatCurrency(summary.totalExpenses ?? 0)}
          </p>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Purchases + BL + Overhead + Labour
          </p>
        </div>

        {/* Card 3: Net Profit / Loss */}
        <div
          className={`rounded-xl border p-4 shadow-sm transition-all ${
            isProfit
              ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900/40 dark:bg-emerald-950/10"
              : "border-rose-200 bg-rose-50/50 dark:border-rose-900/40 dark:bg-rose-950/10"
          }`}
        >
          <div className="flex items-center justify-between">
            <p
              className={`text-[11px] font-bold uppercase tracking-wider ${
                isProfit
                  ? "text-emerald-800 dark:text-emerald-300"
                  : "text-rose-800 dark:text-rose-300"
              }`}
            >
              Net Profit / (Loss)
            </p>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-black uppercase tracking-wider ${
                isProfit
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200"
                  : "bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200"
              }`}
            >
              {isProfit ? "NET PROFIT" : "NET LOSS"}
            </span>
          </div>
          <p
            className={`mt-2 text-xl sm:text-2xl font-black ${
              isProfit
                ? "text-emerald-700 dark:text-emerald-300"
                : "text-rose-700 dark:text-rose-300"
            }`}
          >
            {formatCurrency(summary.netProfitOrLoss ?? 0)}
          </p>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            {isProfit ? "Positive Financial Result" : "Negative Financial Result"}
          </p>
        </div>

        {/* Card 4: Profit Margin (%) */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Profit Margin (%)
            </p>
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
              <i className="material-icons text-lg">pie_chart</i>
            </span>
          </div>
          <p
            className={`mt-2 text-xl sm:text-2xl font-black ${
              isProfit
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400"
            }`}
          >
            {Number(summary.profitMarginPercentage ?? 0).toFixed(2)} %
          </p>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Margin on Total Revenue
          </p>
        </div>
      </div>

      {/* P&L Financial Statement Summary Table */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
        <div className="border-b border-slate-200 px-4 py-3.5 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/30">
          <div className="flex items-center gap-2">
            <i className="material-icons text-primary-600 text-lg">analytics</i>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white">
              Financial Statement Summary
            </h2>
          </div>
          {(filters.startDate || filters.endDate) && (
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-md">
              Filtered: {filters.startDate || "Start"} to {filters.endDate || "End"}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-100/70 dark:bg-slate-800/60 text-[10px] font-black uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-4 py-3">Sr.</th>
                <th className="px-4 py-3">Financial Line Item</th>
                <th className="px-4 py-3">Description / Filter Type</th>
                <th className="px-4 py-3 text-right">Amount (RS)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                <td className="px-4 py-3 font-semibold text-slate-500">1</td>
                <td className="px-4 py-3 font-bold text-emerald-700 dark:text-emerald-400">
                  Total Sales Revenue
                </td>
                <td className="px-4 py-3 text-slate-500">Filtered by Invoice Date</td>
                <td className="px-4 py-3 text-right font-bold text-emerald-700 dark:text-emerald-400">
                  + {formatCurrency(summary.totalRevenue ?? summary.totalSalesGrandTotal ?? 0)}
                </td>
              </tr>
              <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                <td className="px-4 py-3 font-semibold text-slate-500">2</td>
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                  Total Purchases
                </td>
                <td className="px-4 py-3 text-slate-500">Filtered by Bill Date</td>
                <td className="px-4 py-3 text-right font-medium text-rose-600 dark:text-rose-400">
                  - {formatCurrency(summary.totalPurchaseAmountInRS ?? 0)}
                </td>
              </tr>
              <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                <td className="px-4 py-3 font-semibold text-slate-500">3</td>
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                  Expense Master (BL-wise Expenses)
                </td>
                <td className="px-4 py-3 text-slate-500">Custom BL Charges</td>
                <td className="px-4 py-3 text-right font-medium text-rose-600 dark:text-rose-400">
                  - {formatCurrency(summary.totalExpenseMasterAmount ?? 0)}
                </td>
              </tr>
              <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                <td className="px-4 py-3 font-semibold text-slate-500">4</td>
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                  Overhead Expenses
                </td>
                <td className="px-4 py-3 text-slate-500">Operational &amp; General Overheads</td>
                <td className="px-4 py-3 text-right font-medium text-rose-600 dark:text-rose-400">
                  - {formatCurrency(summary.totalOverheadExpenseAmount ?? 0)}
                </td>
              </tr>
              <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                <td className="px-4 py-3 font-semibold text-slate-500">5</td>
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                  Total Labour Costs
                </td>
                <td className="px-4 py-3 text-slate-500">Stock Processing Labour Expenses</td>
                <td className="px-4 py-3 text-right font-medium text-rose-600 dark:text-rose-400">
                  - {formatCurrency(summary.totalLabourCost ?? 0)}
                </td>
              </tr>

              {/* Total Expenses Row */}
              <tr className="bg-slate-50 dark:bg-slate-800/70 font-bold border-t-2 border-slate-200 dark:border-slate-700">
                <td className="px-4 py-3 text-slate-400">•</td>
                <td className="px-4 py-3 uppercase tracking-wider text-slate-800 dark:text-slate-200">
                  TOTAL EXPENSES
                </td>
                <td className="px-4 py-3 text-slate-500 text-[11px] italic">
                  Sum of Purchases, BL, Overhead &amp; Labour Costs
                </td>
                <td className="px-4 py-3 text-right text-sm font-black text-rose-600 dark:text-rose-400">
                  {formatCurrency(summary.totalExpenses ?? 0)}
                </td>
              </tr>

              {/* Net Profit / Loss Final Summary Row */}
              <tr
                className={`${
                  isProfit
                    ? "bg-emerald-50/70 dark:bg-emerald-950/30"
                    : "bg-rose-50/70 dark:bg-rose-950/30"
                } border-t-2 ${
                  isProfit
                    ? "border-emerald-300 dark:border-emerald-800"
                    : "border-rose-300 dark:border-rose-800"
                }`}
              >
                <td className="px-4 py-3.5 font-black text-slate-400">★</td>
                <td className="px-4 py-3.5 text-sm font-black uppercase tracking-wider flex items-center gap-2">
                  <span
                    className={
                      isProfit
                        ? "text-emerald-800 dark:text-emerald-300"
                        : "text-rose-800 dark:text-rose-300"
                    }
                  >
                    NET PROFIT / (LOSS)
                  </span>
                  <span
                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-black ${
                      isProfit
                        ? "bg-emerald-200 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100"
                        : "bg-rose-200 text-rose-900 dark:bg-rose-800 dark:text-rose-100"
                    }`}
                  >
                    {isProfit ? "PROFIT" : "LOSS"}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-[11px] font-bold text-slate-600 dark:text-slate-400">
                  Margin: {Number(summary.profitMarginPercentage ?? 0).toFixed(2)} %
                </td>
                <td
                  className={`px-4 py-3.5 text-right text-base font-black ${
                    isProfit
                      ? "text-emerald-700 dark:text-emerald-300"
                      : "text-rose-700 dark:text-rose-300"
                  }`}
                >
                  {formatCurrency(summary.netProfitOrLoss ?? 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Breakdown Tabs (5 Components) */}
      <div className="space-y-4">
        <div className="flex border-b border-slate-200 dark:border-slate-800 overflow-x-auto no-scrollbar gap-2">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold transition-all border-b-2 whitespace-nowrap ${
                  isActive
                    ? "border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/10 rounded-t-lg"
                    : "border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                <i className="material-icons text-base">{tab.icon}</i>
                <span>{tab.label}</span>
                <span
                  className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-black ${
                    isActive
                      ? "bg-primary-600 text-white dark:bg-primary-400 dark:text-slate-950"
                      : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab 1: Purchases */}
        {activeTab === "purchases" && (
          <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <DataTable
              columns={purchaseColumns}
              data={reportData.purchases}
              isLoading={isLoading}
              searchPlaceholder="Search purchase bills..."
              pageSize={10}
              scrollHeight="calc(100vh - 500px)"
            />
          </div>
        )}

        {/* Tab 2: Expense Master */}
        {activeTab === "expenseMaster" && (
          <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <DataTable
              columns={expenseMasterColumns}
              data={reportData.expenseMaster}
              isLoading={isLoading}
              searchPlaceholder="Search BL expenses..."
              pageSize={10}
              scrollHeight="calc(100vh - 500px)"
            />
          </div>
        )}

        {/* Tab 3: Overhead Expenses */}
        {activeTab === "overheadExpenses" && (
          <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <DataTable
              columns={overheadColumns}
              data={reportData.overheadExpenses}
              isLoading={isLoading}
              searchPlaceholder="Search overhead expenses..."
              pageSize={10}
              scrollHeight="calc(100vh - 500px)"
            />
          </div>
        )}

        {/* Tab 4: Labour Expenses */}
        {activeTab === "labourExpenses" && (
          <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <DataTable
              columns={labourColumns}
              data={reportData.labourExpenses}
              isLoading={isLoading}
              searchPlaceholder="Search labour processing..."
              pageSize={10}
              scrollHeight="calc(100vh - 500px)"
            />
          </div>
        )}

        {/* Tab 5: Total Sales */}
        {activeTab === "sales" && (
          <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <DataTable
              columns={salesColumns}
              data={reportData.sales}
              isLoading={isLoading}
              searchPlaceholder="Search sales records..."
              pageSize={10}
              scrollHeight="calc(100vh - 500px)"
              expandable
              expandedRows={expandedSalesRows}
              renderExpandableContent={renderExpandableSalesContent}
              onRowClick={(item) => toggleSalesRow(item.sellingMasterId)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default PnLReportPage;
